<script>
    import { onMount } from 'svelte';
    import { chatbotAPI, aiProvidersAPI } from './api.js';

    let chatbots = [];
    let providers = [];
    let loading = false;
    let error = null;

    // Modal state
    let showModal = false;
    let editingChatbot = null;
    let formData = {
        name: '',
        ai_provider_id: '',
        system_prompt: '',
        greeting_message: '',
        fallback_message: '',
        use_knowledge_base: true,
        auto_respond: false,
        response_delay: 0,
        max_tokens: 1000,
        temperature: 0.7,
        is_active: true
    };

    const temperaturePresets = [
        { value: 0.3, label: 'Preciso (0.3)', description: 'Respuestas más consistentes y predecibles' },
        { value: 0.7, label: 'Balanceado (0.7)', description: 'Balance entre creatividad y precisión' },
        { value: 1.0, label: 'Creativo (1.0)', description: 'Respuestas más variadas y creativas' }
    ];

    const systemPromptTemplates = [
        {
            name: 'Asistente de Soporte',
            prompt: 'Eres un asistente de soporte técnico amigable y profesional. Ayudas a los clientes a resolver problemas, respondes preguntas sobre productos y servicios, y proporcionas información útil. Siempre sé cortés, claro y conciso.'
        },
        {
            name: 'Asistente de Ventas',
            prompt: 'Eres un asistente de ventas entusiasta y conocedor. Tu objetivo es ayudar a los clientes a encontrar los productos o servicios que mejor se adapten a sus necesidades, responder preguntas sobre precios y características, y guiarlos en el proceso de compra.'
        },
        {
            name: 'Asistente General',
            prompt: 'Eres un asistente virtual amigable y útil. Respondes preguntas, proporcionas información y ayudas a los usuarios de manera profesional. Usa la base de conocimiento cuando esté disponible para dar respuestas precisas.'
        }
    ];

    onMount(() => {
        loadData();
    });

    async function loadData() {
        loading = true;
        error = null;
        try {
            const [chatbotsData, providersData] = await Promise.all([
                chatbotAPI.getAll(),
                aiProvidersAPI.getAll()
            ]);
            chatbots = chatbotsData;
            providers = providersData.filter(p => p.is_active);
        } catch (err) {
            error = err.message || 'Error al cargar datos';
            console.error('Error loading data:', err);
        } finally {
            loading = false;
        }
    }

    function openCreateModal() {
        editingChatbot = null;
        formData = {
            name: '',
            ai_provider_id: providers.length > 0 ? providers[0].id : '',
            system_prompt: systemPromptTemplates[2].prompt,
            greeting_message: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?',
            fallback_message: 'Disculpa, no tengo esa información. Un agente humano te contactará pronto.',
            use_knowledge_base: true,
            auto_respond: false,
            response_delay: 0,
            max_tokens: 1000,
            temperature: 0.7,
            is_active: true
        };
        showModal = true;
    }

    function openEditModal(chatbot) {
        editingChatbot = chatbot;
        formData = {
            name: chatbot.name,
            ai_provider_id: chatbot.ai_provider_id,
            system_prompt: chatbot.system_prompt || '',
            greeting_message: chatbot.greeting_message || '',
            fallback_message: chatbot.fallback_message || '',
            use_knowledge_base: chatbot.use_knowledge_base,
            auto_respond: chatbot.auto_respond,
            response_delay: chatbot.response_delay || 0,
            max_tokens: chatbot.max_tokens || 1000,
            temperature: chatbot.temperature || 0.7,
            is_active: chatbot.is_active
        };
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        editingChatbot = null;
    }

    function applyTemplate(template) {
        formData.system_prompt = template.prompt;
    }

    async function saveChatbot() {
        if (!formData.name || !formData.ai_provider_id) {
            alert('Por favor complete nombre y seleccione un proveedor de IA');
            return;
        }

        loading = true;
        error = null;

        try {
            const chatbotData = { ...formData };

            if (editingChatbot) {
                await chatbotAPI.update(editingChatbot.id, chatbotData);
            } else {
                await chatbotAPI.create(chatbotData);
            }

            closeModal();
            await loadData();
        } catch (err) {
            error = err.message || 'Error al guardar el chatbot';
            console.error('Error saving chatbot:', err);
        } finally {
            loading = false;
        }
    }

    async function toggleActive(chatbot) {
        loading = true;
        error = null;

        try {
            await chatbotAPI.update(chatbot.id, { is_active: !chatbot.is_active });
            await loadData();
        } catch (err) {
            error = err.message || 'Error al actualizar el chatbot';
            console.error('Error toggling chatbot:', err);
        } finally {
            loading = false;
        }
    }

    function getProviderName(providerId) {
        const provider = providers.find(p => p.id === providerId);
        return provider ? provider.name : 'Desconocido';
    }

    function getProviderType(providerId) {
        const provider = providers.find(p => p.id === providerId);
        return provider ? provider.provider_type : '';
    }
</script>

<style>
    .chatbots-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .header h1 {
        margin: 0;
        color: #333;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-edit {
        background: #ff9800;
        color: white;
        padding: 8px 16px;
        font-size: 13px;
    }

    .btn-edit:hover {
        background: #f57c00;
    }

    .btn-toggle {
        padding: 8px 16px;
        font-size: 13px;
    }

    .btn-toggle.active {
        background: #4caf50;
        color: white;
    }

    .btn-toggle.inactive {
        background: #757575;
        color: white;
    }

    .error-message {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
        border-left: 4px solid #c62828;
    }

    .chatbots-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .chatbot-card {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s;
        position: relative;
    }

    .chatbot-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        transform: translateY(-2px);
    }

    .chatbot-card.inactive {
        opacity: 0.6;
        background: #f5f5f5;
    }

    .chatbot-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 15px;
    }

    .chatbot-title {
        font-size: 20px;
        font-weight: 600;
        color: #333;
        margin: 0;
    }

    .status-badge {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .status-badge.active {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .status-badge.inactive {
        background: #eeeeee;
        color: #616161;
    }

    .chatbot-provider {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 15px;
        padding: 10px;
        background: #f5f5f5;
        border-radius: 6px;
    }

    .provider-icon {
        font-size: 20px;
    }

    .provider-info {
        flex: 1;
    }

    .provider-name {
        font-weight: 600;
        color: #333;
        font-size: 14px;
    }

    .provider-type {
        font-size: 12px;
        color: #757575;
        text-transform: capitalize;
    }

    .chatbot-details {
        margin-bottom: 15px;
    }

    .detail-row {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
    }

    .detail-label {
        color: #757575;
        font-weight: 500;
    }

    .detail-value {
        color: #333;
        font-weight: 600;
    }

    .feature-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 15px;
    }

    .feature-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 6px 12px;
        background: #e3f2fd;
        color: #1976d2;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
    }

    .feature-badge.enabled {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .feature-badge.disabled {
        background: #fafafa;
        color: #9e9e9e;
    }

    .chatbot-actions {
        display: flex;
        gap: 8px;
        margin-top: 15px;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 30px;
        width: 90%;
        max-width: 700px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal h2 {
        margin: 0 0 25px 0;
        color: #333;
    }

    .form-section {
        margin-bottom: 25px;
        padding-bottom: 20px;
        border-bottom: 1px solid #e0e0e0;
    }

    .form-section:last-of-type {
        border-bottom: none;
    }

    .form-section h3 {
        margin: 0 0 15px 0;
        color: #555;
        font-size: 16px;
    }

    .form-group {
        margin-bottom: 18px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group textarea {
        min-height: 100px;
        resize: vertical;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .checkbox-group label {
        margin: 0;
        cursor: pointer;
    }

    .range-group {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .range-group input[type="range"] {
        flex: 1;
    }

    .range-value {
        min-width: 60px;
        text-align: center;
        font-weight: 600;
        color: #2196f3;
    }

    .templates {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
        flex-wrap: wrap;
    }

    .template-btn {
        padding: 8px 14px;
        border: 1px solid #2196f3;
        background: white;
        color: #2196f3;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;
    }

    .template-btn:hover {
        background: #2196f3;
        color: white;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 25px;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #757575;
    }

    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #757575;
    }

    .empty-state h3 {
        color: #333;
        margin-bottom: 10px;
    }

    .warning-box {
        background: #fff3e0;
        border-left: 4px solid #ff9800;
        padding: 12px 16px;
        margin-bottom: 20px;
        border-radius: 4px;
    }

    .warning-box p {
        margin: 0;
        color: #e65100;
        font-size: 13px;
    }
</style>

<div class="chatbots-container">
    <div class="header">
        <h1>🤖 Configuración de Chatbots</h1>
        <button class="btn btn-primary" on:click={openCreateModal}>
            + Nuevo Chatbot
        </button>
    </div>

    {#if error}
        <div class="error-message">{error}</div>
    {/if}

    {#if providers.length === 0}
        <div class="warning-box">
            <p>⚠️ No hay proveedores de IA activos. Por favor, active al menos un proveedor de IA antes de crear un chatbot.</p>
        </div>
    {/if}

    {#if loading}
        <div class="loading">Cargando...</div>
    {:else if chatbots.length === 0}
        <div class="empty-state">
            <h3>No hay chatbots configurados</h3>
            <p>Crea tu primer chatbot para comenzar a automatizar conversaciones.</p>
        </div>
    {:else}
        <div class="chatbots-grid">
            {#each chatbots as chatbot}
                <div class="chatbot-card" class:inactive={!chatbot.is_active}>
                    <div class="chatbot-header">
                        <h3 class="chatbot-title">{chatbot.name}</h3>
                        <span class="status-badge" class:active={chatbot.is_active} class:inactive={!chatbot.is_active}>
                            {chatbot.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                    </div>

                    <div class="chatbot-provider">
                        <div class="provider-icon">🤖</div>
                        <div class="provider-info">
                            <div class="provider-name">{getProviderName(chatbot.ai_provider_id)}</div>
                            <div class="provider-type">{getProviderType(chatbot.ai_provider_id)}</div>
                        </div>
                    </div>

                    <div class="feature-badges">
                        <span class="feature-badge" class:enabled={chatbot.use_knowledge_base} class:disabled={!chatbot.use_knowledge_base}>
                            {chatbot.use_knowledge_base ? '✓' : '○'} Base de conocimiento
                        </span>
                        <span class="feature-badge" class:enabled={chatbot.auto_respond} class:disabled={!chatbot.auto_respond}>
                            {chatbot.auto_respond ? '✓' : '○'} Auto-respuesta
                        </span>
                    </div>

                    <div class="chatbot-details">
                        <div class="detail-row">
                            <span class="detail-label">Temperatura</span>
                            <span class="detail-value">{chatbot.temperature}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Max Tokens</span>
                            <span class="detail-value">{chatbot.max_tokens}</span>
                        </div>
                        {#if chatbot.response_delay > 0}
                            <div class="detail-row">
                                <span class="detail-label">Retraso de respuesta</span>
                                <span class="detail-value">{chatbot.response_delay}s</span>
                            </div>
                        {/if}
                    </div>

                    <div class="chatbot-actions">
                        <button
                            class="btn btn-toggle"
                            class:active={chatbot.is_active}
                            class:inactive={!chatbot.is_active}
                            on:click={() => toggleActive(chatbot)}
                        >
                            {chatbot.is_active ? '✓ Activo' : '○ Inactivo'}
                        </button>
                        <button class="btn btn-edit" on:click={() => openEditModal(chatbot)}>
                            ✏️ Editar
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>{editingChatbot ? 'Editar Chatbot' : 'Nuevo Chatbot'}</h2>

            <form on:submit|preventDefault={saveChatbot}>
                <!-- Basic Configuration -->
                <div class="form-section">
                    <h3>⚙️ Configuración Básica</h3>

                    <div class="form-group">
                        <label for="name">Nombre del Chatbot *</label>
                        <input
                            id="name"
                            type="text"
                            bind:value={formData.name}
                            placeholder="Ej: Asistente de Soporte 24/7"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="ai_provider">Proveedor de IA *</label>
                        <select id="ai_provider" bind:value={formData.ai_provider_id} required>
                            <option value="">Seleccionar proveedor...</option>
                            {#each providers as provider}
                                <option value={provider.id}>
                                    {provider.name} ({provider.provider_type})
                                </option>
                            {/each}
                        </select>
                        <small>Solo se muestran proveedores activos.</small>
                    </div>

                    <div class="checkbox-group">
                        <input
                            id="is_active"
                            type="checkbox"
                            bind:checked={formData.is_active}
                        />
                        <label for="is_active">Chatbot activo y disponible</label>
                    </div>
                </div>

                <!-- AI Behavior -->
                <div class="form-section">
                    <h3>🧠 Comportamiento de IA</h3>

                    <div class="form-group">
                        <label for="system_prompt">Prompt del Sistema</label>
                        <div class="templates">
                            {#each systemPromptTemplates as template}
                                <button
                                    type="button"
                                    class="template-btn"
                                    on:click={() => applyTemplate(template)}
                                >
                                    {template.name}
                                </button>
                            {/each}
                        </div>
                        <textarea
                            id="system_prompt"
                            bind:value={formData.system_prompt}
                            placeholder="Define cómo debe comportarse el chatbot..."
                        ></textarea>
                        <small>Instrucciones que guían el comportamiento del chatbot.</small>
                    </div>

                    <div class="form-group">
                        <label for="temperature">Temperatura: {formData.temperature}</label>
                        <div class="range-group">
                            <input
                                id="temperature"
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                bind:value={formData.temperature}
                            />
                            <span class="range-value">{formData.temperature}</span>
                        </div>
                        <small>
                            {#if formData.temperature < 0.5}
                                Respuestas más precisas y consistentes
                            {:else if formData.temperature < 0.8}
                                Balance entre creatividad y precisión
                            {:else}
                                Respuestas más creativas y variadas
                            {/if}
                        </small>
                    </div>

                    <div class="form-group">
                        <label for="max_tokens">Tokens Máximos</label>
                        <input
                            id="max_tokens"
                            type="number"
                            bind:value={formData.max_tokens}
                            min="100"
                            max="4000"
                            step="100"
                        />
                        <small>Longitud máxima de las respuestas (100-4000).</small>
                    </div>
                </div>

                <!-- Messages -->
                <div class="form-section">
                    <h3>💬 Mensajes</h3>

                    <div class="form-group">
                        <label for="greeting_message">Mensaje de Bienvenida</label>
                        <textarea
                            id="greeting_message"
                            bind:value={formData.greeting_message}
                            placeholder="¡Hola! ¿En qué puedo ayudarte?"
                        ></textarea>
                        <small>Mensaje inicial que el chatbot envía al activarse.</small>
                    </div>

                    <div class="form-group">
                        <label for="fallback_message">Mensaje de Respaldo</label>
                        <textarea
                            id="fallback_message"
                            bind:value={formData.fallback_message}
                            placeholder="No tengo esa información. Un agente te contactará."
                        ></textarea>
                        <small>Mensaje cuando el chatbot no puede responder.</small>
                    </div>
                </div>

                <!-- Features -->
                <div class="form-section">
                    <h3>✨ Características</h3>

                    <div class="checkbox-group">
                        <input
                            id="use_knowledge_base"
                            type="checkbox"
                            bind:checked={formData.use_knowledge_base}
                        />
                        <label for="use_knowledge_base">Usar base de conocimiento</label>
                    </div>
                    <small style="margin-left: 28px; display: block; margin-bottom: 12px;">
                        El chatbot buscará información en la base de conocimiento.
                    </small>

                    <div class="checkbox-group">
                        <input
                            id="auto_respond"
                            type="checkbox"
                            bind:checked={formData.auto_respond}
                        />
                        <label for="auto_respond">Auto-respuesta automática</label>
                    </div>
                    <small style="margin-left: 28px; display: block; margin-bottom: 12px;">
                        El chatbot responderá automáticamente a nuevos mensajes.
                    </small>

                    <div class="form-group">
                        <label for="response_delay">Retraso de Respuesta (segundos)</label>
                        <input
                            id="response_delay"
                            type="number"
                            bind:value={formData.response_delay}
                            min="0"
                            max="30"
                        />
                        <small>Tiempo de espera antes de responder (0-30 segundos).</small>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading || providers.length === 0}>
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
